import useOffer from 'hooks/Offers/useOffer'
import { useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getThemeClassName } from 'lib/theme/themeUtils'
import PageBanner from 'LuxKit/Banners/PageBanner'
import React, { useCallback, useMemo, useState } from 'react'
import { matchPath, useRouteMatch } from 'react-router'
import styled from 'styled-components'
import { useCurrentSiteTakeoverV2 } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import SiteTakeoverBackgroundImage from './SiteTakeoverBackgroundImage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { rem } from 'polished'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import { useSiteTakeoverTheme } from 'takeovers/utils/hooks/useSiteTakeoverTheme'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import Image from 'components/Common/Image'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'

const Background = styled(SiteTakeoverBackgroundImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
`

const SiteTakeoverBackgroundStyle = styled.div<{ backgroundStyle?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: ${props => props.backgroundStyle};
`

const Content = styled.div`
  transform: translateZ(0);
`

const Banner = styled.div`
  position: relative;
`

const CTAButtonWrapper = styled.div`
  /* Reset to default theme for white button background */
`

const offerPageRoutes: Array<string> = [
  '/:regionCode/offer/:slug/:offerId',
  '/:regionCode/cruises/:offerId',
  '/:regionCode/cruises/:slug/:offerId',
  '/:regionCode/tour/:offerId',
  '/:regionCode/partner/:slug/:offerId',
]

const noBannerRoutes: Array<string> = [
  '/:regionCode/search',
  '/:regionCode/checkout',
  '/:regionCode/account',
  '/:regionCode/booking',
  '/:regionCode/bb/booking',
  '/:regionCode/trip-planner/generate-trip',
  '/:regionCode/campaign'
]

const toursRoutes: Array<string> = [
  '/:regionCode/tours',
  '/:regionCode/tour/:offerId',
]

const invalidExactRoutes: Array<string> = [
  '/:regionCode',
  '/:regionCode/triple-points',
]

const homePageRoutes: Array<string> = [
  '/:regionCode',
]

function SiteTakeoverPageBanner() {
  const match = useRouteMatch<{ offerId: string }>(offerPageRoutes)
  const [offer] = useOffer(match?.params.offerId)
  const currentPath = useAppSelector(state => state.router.location.pathname)

  // Call all hooks unconditionally
  const siteTakeoverV2 = useCurrentSiteTakeoverV2()
  const siteTakeover = siteTakeoverV2

  const siteTakeoverOnTourV2 = useCurrentSiteTakeoverV2({ heroVertical: 'tours' })
  const siteTakeoverOnTour = siteTakeoverOnTourV2

  const showingOfferBanner = !!(offer && siteTakeover?.offerTypes?.has(offer.type))
  const [isDismissed, setIsDismissed] = useState(false)
  const labSiteTakeoverTheme = useSiteTakeoverTheme()

  const onDismissHandler = useCallback(() => {
    setIsDismissed(true)
  }, [])

  const hide = useMemo((): boolean => {
    const exceptedRoutes: Array<string> = siteTakeover?.siteWide?.banner.exceptedRoutes ?? EmptyArray
    // hide on:
    // - The home page (has its own banner)
    // - Search page results (has it's own banner)
    // - Would show the offer page banner  (has it's own banner)
    // - Any of the other no banner routes (shouldn't show banners)
    // - When tour has it's own takeover
    return showingOfferBanner ||
      !!matchPath(currentPath, { path: homePageRoutes, exact: true }) ||
      !!matchPath(currentPath, { path: invalidExactRoutes, exact: true }) ||
      (!!exceptedRoutes.length && !!matchPath(currentPath, { path: exceptedRoutes })) ||
      !!matchPath(currentPath, { path: noBannerRoutes }) ||
      (!!siteTakeoverOnTour && !!matchPath(currentPath, { path: toursRoutes }))
  }, [currentPath, showingOfferBanner, siteTakeover, siteTakeoverOnTour])

  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const siteTakeoverBannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  const useLab = !!siteTakeoverBannerContentPayload
  const useCustom = !useLab && !!siteTakeover?.siteWide?.banner

  const shouldHideOnMobile = useMemo(() => {
    return !!siteTakeover?.siteWide?.banner?.hideOnOfferPagesForMobileScreenSize &&
      !!matchPath(currentPath, { path: offerPageRoutes })
  }, [siteTakeover, currentPath])

  if (hide || isDismissed) {
    return null
  }

  // Early return if neither lab nor custom config is available
  if (!useLab && !useCustom) {
    return null
  }

  // Extract values for lab config
  const title = siteTakeoverBannerContentPayload?.title
  const subtitle = siteTakeoverBannerContentPayload?.subtitle
  const ctaLink = siteTakeoverBannerContentPayload?.ctaLink
  const logoRef = labSiteTakeoverTheme?.logoRef
  const backgroundStyle = labSiteTakeoverTheme?.backgroundStyle
  const paletteType = labSiteTakeoverTheme?.palette ?? 'default'

  // Determine banner content based on config type
  const bannerContent = useLab ? (
    <>
      <CSSBreakpoint max="mobile">
        <Group direction="vertical" verticalAlign="start">
          <BodyText variant="medium" weight="bold">
            {title}
          </BodyText>
          <BodyText variant="small">
            {subtitle}
            {ctaLink && (
              <>
                <span style={{ marginRight: rem(8) }} />
                <TextLink to={ctaLink} wrap="no-wrap">View offers</TextLink>
              </>
            )}
          </BodyText>
        </Group>
      </CSSBreakpoint>

      <CSSBreakpoint min="tablet">
        <Group direction="horizontal" gap={12} verticalAlign="center">
          {logoRef && <Image id={logoRef} dpr={2} width={32} height={32} />}

          <BodyText variant="medium">
            <b>{title}</b>
            <span style={{ marginRight: rem(8) }} />
            {subtitle}
          </BodyText>

          {ctaLink && (
            <CTAButtonWrapper className={getThemeClassName({ mobile: 'inverse' })}>
              <TextButton to={ctaLink} size="small" kind="primary">
                View offers
              </TextButton>
            </CTAButtonWrapper>
          )}
        </Group>
      </CSSBreakpoint>
    </>
  ) : siteTakeover?.siteWide?.banner.description

  // Determine PageBanner props based on config type
  const pageBannerProps = useCustom && siteTakeover?.siteWide?.banner ? {
    primaryActionButtonProps: siteTakeover.siteWide.banner.bannerAction,
    icon: siteTakeover.siteWide.banner.logo,
    align: siteTakeover.siteWide.banner.align ?? 'start',
    tabletAlign: siteTakeover.siteWide.banner.tabletAlign ?? 'center',
  } : {
    align: 'start' as const,
    tabletAlign: 'center' as const,
  }

  return (
    <CSSBreakpoint min={shouldHideOnMobile ? 'tablet' : undefined}>
      <Banner>
        {useCustom && <Background background="default" />}
        {useLab && backgroundStyle && <SiteTakeoverBackgroundStyle backgroundStyle={backgroundStyle} />}
        <Content className={getThemeClassName({
          mobile: useCustom ? siteTakeover?.palette : paletteType
        })}
        >
          <PageBanner
            {...pageBannerProps}
            density="super-compact"
            kind="none"
            onDismiss={onDismissHandler}
          >
            {bannerContent}
          </PageBanner>
        </Content>
      </Banner>
    </CSSBreakpoint>
  )
}

export default SiteTakeoverPageBanner
